import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import {
  produtividadeListSchema,
  produtividadeSchema,
  type ProdutividadeCreateInput,
  type ProdutividadeUpdateInput,
} from './schemas';

const keys = {
  list: (cpf: string) => ['produtividade', 'list', cpf] as const,
  detail: (id: number | string) => ['produtividade', 'detail', String(id)] as const,
};

export function useProdutividadeCliente(cpf: string | null) {
  return useQuery({
    queryKey: keys.list(cpf ?? ''),
    queryFn: ({ signal }) =>
      api(`/api/v2/clientes/${encodeURIComponent(cpf!)}/produtividade-historico`, {
        schema: produtividadeListSchema,
        signal,
      }),
    enabled: !!cpf,
    staleTime: 30_000,
  });
}

export function useCreateProdutividade(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProdutividadeCreateInput) =>
      api('/api/v2/produtividade-historico', {
        method: 'POST',
        body: body as unknown as Record<string, unknown>,
        schema: produtividadeSchema,
      }),
    onSuccess: () => {
      if (cpf) void qc.invalidateQueries({ queryKey: keys.list(cpf) });
    },
  });
}

export function useUpdateProdutividade(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number } & ProdutividadeUpdateInput) =>
      api(`/api/v2/produtividade-historico/${id}`, {
        method: 'PATCH',
        body: body as unknown as Record<string, unknown>,
        schema: produtividadeSchema,
      }),
    onSuccess: () => {
      if (cpf) void qc.invalidateQueries({ queryKey: keys.list(cpf) });
    },
  });
}

export function useDeleteProdutividade(cpf: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api(`/api/v2/produtividade-historico/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      if (cpf) void qc.invalidateQueries({ queryKey: keys.list(cpf) });
    },
  });
}
